import type { PaletteCommand } from 'features/project/features/command-palette/types'
import CatalogSvgIcon from 'features/project/partials/project-layout/components/navigation/components/catalog-svg-icon/CatalogSvgIcon'
import DashboardsSvgIcon from 'features/project/partials/project-layout/components/navigation/components/dashboards-svg-icon/DashboardsSvgIcon'
import SavedSvgIcon from 'features/project/partials/project-layout/components/navigation/components/saved-svg-icon/SavedSvgIcon'
import type { SavedView } from 'features/saved-views/types'

// Mirrors CatalogList: these two theme values are synthetic sentiment
// buckets, not real catalog themes.
const EXCLUDED_THEME_VALUES = new Set([
  'Critics / Detractors',
  'Fans / Attractors',
])

export type CatalogEntityLists = {
  brands: string[]
  products: string[]
  themes: string[]
  sources: string[]
}

export type EntityCommandInputs = {
  dashboards: Array<{ uuid: string; name: string }>
  savedViews: SavedView[]
  catalog: CatalogEntityLists
  openDashboard: (uuid: string) => void
  applySavedView: (view: SavedView) => void
  openCatalogItem: (collection: string, name: string) => void
}

const catalogCommands = (
  names: string[],
  collection: string,
  collectionLabel: string,
  openCatalogItem: EntityCommandInputs['openCatalogItem'],
): PaletteCommand[] =>
  // The filter values can repeat a name (e.g. one row per theme cluster).
  [...new Set(names)].map((name) => ({
    id: `catalog:${collection}:${name}`,
    label: name,
    context: ['Go to Catalog', collectionLabel],
    icon: CatalogSvgIcon,
    section: 'Catalog' as const,
    keywords: ['catalog', 'jump'],
    searchOnly: true,
    execute: () => openCatalogItem(collection, name),
  }))

export const buildEntityCommands = (
  inputs: EntityCommandInputs,
): PaletteCommand[] => [
  ...inputs.dashboards.map((dashboard) => ({
    id: `dashboard:${dashboard.uuid}`,
    label: dashboard.name,
    context: ['Go to Dashboards'],
    icon: DashboardsSvgIcon,
    section: 'Dashboards' as const,
    keywords: ['jump', 'open', 'dashboard'],
    searchOnly: true,
    execute: () => inputs.openDashboard(dashboard.uuid),
  })),
  ...inputs.savedViews.map((view) => ({
    id: `saved-view:${view.uuid}`,
    label: view.name,
    context: ['Load saved view'],
    icon: SavedSvgIcon,
    section: 'Saved views' as const,
    keywords: ['jump', 'load', 'filter', view.page],
    searchOnly: true,
    execute: () => inputs.applySavedView(view),
  })),
  ...catalogCommands(
    inputs.catalog.brands,
    'brands',
    'Brands',
    inputs.openCatalogItem,
  ),
  ...catalogCommands(
    inputs.catalog.products,
    'products',
    'Products',
    inputs.openCatalogItem,
  ),
  ...catalogCommands(
    inputs.catalog.themes.filter((name) => !EXCLUDED_THEME_VALUES.has(name)),
    'themes',
    'Themes',
    inputs.openCatalogItem,
  ),
  ...catalogCommands(
    inputs.catalog.sources,
    'sources',
    'Sources',
    inputs.openCatalogItem,
  ),
]
